import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

import { storage } from './firebaseConfig.js';
import { ImageRepository } from '../repositories/ImageRepository.js';

/**
 * Firebase Storage implementation of ImageRepository.
 *
 * Profile images are stored at: profile-images/{userId}/{filename}
 * Images are resized client-side before upload (max 800x800, JPEG 0.8).
 */
export class FirebaseImageRepo extends ImageRepository {

  /** @returns {Promise<string>} - Download URL of uploaded image */
  async uploadProfileImage(userId, file) {
    const resized = await _resizeImage(file, 800, 800, 0.8);

    const filename = `profile-${Date.now()}.jpg`;
    const storageRef = ref(storage, `profile-images/${userId}/${filename}`);

    await uploadBytes(storageRef, resized, { contentType: 'image/jpeg' });
    return getDownloadURL(storageRef);
  }

  /** @returns {Promise<void>} */
  async deleteProfileImage(userId) {
    // We use a convention-based path; list isn't available in the client SDK,
    // so we rely on the caller knowing the exact path. For a more robust
    // approach, store the full storage path in the resume doc.
    // Here we delete the folder prefix — but Firebase client SDK doesn't
    // support folder deletes, so we need the exact ref.
    //
    // In practice, the calling code should pass the stored URL and we'd
    // extract the ref. For now, we attempt to delete the known path pattern.
    //
    // A better approach used here: the caller provides the URL via the
    // resume's profileImageUrl. We parse the storage path from it.
    throw new Error(
      'deleteProfileImage requires the full storage path. ' +
      'Use deleteProfileImageByRef(storageRefPath) instead, or pass the download URL.'
    );
  }

  /**
   * Delete a profile image by its full storage path.
   * @param {string} storagePath - e.g. "profile-images/abc123/profile-1679000000.jpg"
   * @returns {Promise<void>}
   */
  async deleteProfileImageByPath(storagePath) {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  }
}

// ---------------------------------------------------------------------------
// Client-side image resize
// ---------------------------------------------------------------------------

/**
 * Resize an image file to fit within maxWidth x maxHeight, output as JPEG blob.
 *
 * @param {File|Blob} file - Source image
 * @param {number} maxWidth
 * @param {number} maxHeight
 * @param {number} quality - JPEG quality 0..1
 * @returns {Promise<Blob>}
 */
function _resizeImage(file, maxWidth, maxHeight, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Scale down proportionally if larger than max dimensions
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas toBlob returned null'));
        },
        'image/jpeg',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = url;
  });
}
